<template>
  <modal
    size="full"
    :title="isCertUpload ? 'Upload Certs' : 'Upload Collection'"
    :open="open"
    @close="closeModal"
  >
    <p class="description">
      <span>In order to get {{ isCertUpload ? 'your PSA, BGS, SGC or CGC cards uploaded' : 'your collection uploaded' }} accurately we need you to format your spreadsheet.</span>
      <a :href="sampleUploadUrl" target="_blank">
        Look at a sample spreadsheet here for an example.
      </a>
      <span>It's important that you name the column headers properly but the order of columns doesn't matter.</span>
      <span>We currently only support .csv file format and you can only upload {{ maxCards }} cards at a time.</span>
    </p>

    <div v-if="output.text" class="output">
      <p>{{ output.text }}</p>
      <p v-if="output.progress">
        Finished uploading {{ output.progress }} of {{ output.cards.length }} cards
      </p>

      <btn
        v-if="isReady"
        :loading="saving"
        @click="submit"
      >
        Upload
      </btn>
    </div>

    <div v-show="!loading && !isReady" class="file-upload">
      <input
        ref="input"
        type="file"
        accept=".csv"
        @change="handleUpload"
      />

      <div class="placeholder">
        <icon class="icon" name="file_upload" />
        <div>{{ isCertUpload ? 'Upload your Graded Certs' : 'Upload your CSV' }}</div>
      </div>
    </div>

    <div v-if="loading" class="loading">
      <spinner />
    </div>
  </modal>
</template>

<script>
import firebase from 'firebase/compat/app';
import { mapState, mapGetters } from 'vuex';
import clone from 'lodash.clonedeep';
import * as Sentry from '@sentry/vue';

import { DEFAULT_CARD_DETAILS, DEFAULT_CARD_COLLECTION } from '@cardladder/shared/constants';
import { getCollectionCardToSave } from '@cardladder/shared/utils/collection.util';
import { getDailySalesFromSalesSearch } from '@cardladder/shared/utils/sales.util';
import { authService, cardService, indexService, searchService } from '@/services';

const GRADING_COMPANY_MAP = {
  psa: 'psa',
  bgs: 'beckett',
  bvg: 'beckett',
  beckett: 'beckett',
  sgc: 'sgc',
  cgc: 'cgc',
  csg: 'cgc',
};

const CSV_MAP = {
  'Date Purchased': {
    column: null,
    format: 'date',
    required: true,
    field: 'datePurchased',
    alternateNames: ['Purchase Date'],
    default: new Date(),
  },
  'Quantity': {
    column: null,
    format: 'number',
    required: false,
    field: 'quantity',
  },
  'Player': {
    column: null,
    format: 'string',
    required: true,
    field: 'player',
  },
  'Year': {
    column: null,
    format: 'string',
    required: true,
    field: 'year',
  },
  'Set': {
    column: null,
    format: 'string',
    required: true,
    field: 'set',
  },
  'Variation': {
    column: null,
    format: 'string',
    required: false,
    field: 'variation',
  },
  'Number': {
    column: null,
    format: 'string',
    required: false,
    field: 'number',
  },
  'Category': {
    column: null,
    format: 'string',
    required: true,
    field: 'category',
  },
  'Condition': {
    column: null,
    format: 'string',
    required: true,
    field: 'condition',
    default: 'Raw',
  },
  'Cert #': {
    column: null,
    format: 'string',
    required: false,
    field: 'cert',
    alternateNames: ['Graded Cert #', 'Slab Serial #'],
  },
  'Investment': {
    column: null,
    format: 'currency',
    required: true,
    field: 'investment',
    default: 100,
  },
  'Estimated Value': {
    column: null,
    format: 'currency',
    required: false,
    field: 'estimatedCurrentValue',
  },
  'Ladder ID': {
    column: null,
    format: 'string',
    required: false,
    field: 'cardId',
  },
  'Notes': {
    column: null,
    format: 'string',
    required: false,
    field: 'notes',
  },
  'Tags': {
    column: null,
    format: 'array',
    required: false,
    field: 'tags',
  },
  'Query': {
    column: null,
    format: 'string',
    required: false,
    field: 'ebayQuery',
    alternateNames: ['Custom Sales Query', 'Sales Query'],
  },
  'Date Sold': {
    column: null,
    format: 'date',
    required: false,
    field: 'dateSold',
  },
  'Sold Price': {
    column: null,
    format: 'currency',
    required: false,
    field: 'priceSold',
  },
  'Image': {
    column: null,
    format: 'string',
    required: false,
    field: 'image',
  },
  'Back Image': {
    column: null,
    format: 'string',
    required: false,
    field: 'imageBack',
  },
};

const CERT_CSV_MAP = {
  'Date Purchased': {
    column: null,
    format: 'date',
    required: true,
    field: 'datePurchased',
    alternateNames: ['Purchase Date'],
    default: new Date(),
  },
  'Cert #': {
    column: null,
    format: 'string',
    required: true,
    field: 'cert',
    alternateNames: ['Graded Cert #', 'Slab Serial #'],
  },
  'Grader': {
    column: null,
    format: 'string',
    required: true,
    field: 'grader',
  },
  'Investment': {
    column: null,
    format: 'currency',
    required: true,
    field: 'investment',
    alternateNames: ['My Cost'],
    default: 100,
  },
  'Estimated Value': {
    column: null,
    format: 'currency',
    required: false,
    field: 'estimatedCurrentValue',
    alternateNames: ['PSA Value'],
  },
  'Notes': {
    column: null,
    format: 'string',
    required: false,
    field: 'notes',
    alternateNames: ['Owner\'s Comments'],
  },
  'Date Sold': {
    column: null,
    format: 'date',
    required: false,
    field: 'dateSold',
  },
  'Sold Price': {
    column: null,
    format: 'currency',
    required: false,
    field: 'priceSold',
  },
};

function csvToArray(text) {
  let p = '', row = [''], ret = [row], i = 0, r = 0, s = !0, l;

  for (l of text) {
    if ('"' === l) {
      if (s && l === p) {
        row[i] += l;
      }

      s = !s;
    } else if (',' === l && s) {
      l = row[++i] = '';
    } else if ('\n' === l && s) {
      if ('\r' === p) {
        row[i] = row[i].slice(0, -1);
      }

      row = ret[++r] = [l = '']; i = 0;
    } else {
      row[i] += l;
    }

    p = l;
  }

  return ret;
}

export default {
  data() {
    return {
      loading: false,
      saving: false,
      output: {},
      open: false,
      maxCards: 500,
      isCertUpload: false,
    };
  },

  mounted() {
    this.$bus.$on('upload-collection', this.handle);
  },

  beforeDestroy() {
    this.$bus.$off('upload-collection', this.handle);
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      collection: (state) => state.collection,
    }),

    ...mapGetters({
      currencyRatio: 'currencyRatio',
    }),

    isReady() {
      return this.output?.cards?.length;
    },

    sampleUploadUrl() {
      return this.isCertUpload ?
        'https://docs.google.com/spreadsheets/d/17L963op5A59il8DYGRi5pTG2xisEGyHoBPk5jelNJyQ/edit?usp=sharing' :
        'https://docs.google.com/spreadsheets/d/1Gxo-ePy3bv9zbLhM1cFvDM65PkXFa1xHcYc1KD1LBTk/edit?usp=sharing';
    },
  },

  methods: {
    handle(isCertUpload = false) {
      this.open = true;
      this.isCertUpload = isCertUpload;
    },

    async handleUpload(event) {
      this.loading = true;
      this.output = {};

      try {
        const file = event.target.files ? event.target.files[0] : null;
        const reader = new FileReader();

        reader.addEventListener('load', (e) => {
          const data = e.target.result;
          const output = csvToArray(data.replace('ï»¿', ''));

          if (output.length > this.maxCards) {
            output.length = this.maxCards + 1;
          }

          if (!output || !output.length || output.length < 2) {
            this.output = { text: 'This file does not have enough rows for data upload. Please fix your file.' };
            this.loading = false;

            return;
          }

          const header = output[0];
          const missingColumns = [];
          const map = this.isCertUpload ? CERT_CSV_MAP : CSV_MAP;

          Object.keys(map).forEach((headerKey) => {
            map[headerKey].column = null;

            const columnIndex = header.findIndex((h) => {
              if (!h || typeof h !== 'string') {
                return false;
              }

              const headerText = h.trim();

              if (headerText === headerKey) {
                return true;
              } else if (map[headerKey].alternateNames) {
                return map[headerKey].alternateNames.includes(headerText);
              }

              return false;
            });

            const columnMap = map[headerKey];

            if (columnIndex > -1) {
              map[headerKey].column = columnIndex;
            } else if (columnMap.required) {
              missingColumns.push(headerKey);
            }
          });

          if (missingColumns.length) {
            this.output = { text: `You are missing the following columns: ${missingColumns.join(', ')}` };
            this.loading = false;

            return;
          }

          let rowIndex;
          let missingData = {};

          const cards = [];

          try {
            for (rowIndex = 1; rowIndex < output.length; rowIndex += 1) {
              const uploadRow = output[rowIndex];

              if (uploadRow.length < 2) {
                continue;
              }

              if (uploadRow.every((str) => str === '')) {
                continue;
              }

              const card = {};

              Object.keys(map).forEach((headerKey) => {
                const columnMap = map[headerKey];

                if (uploadRow[columnMap.column] && uploadRow[columnMap.column] !== '-') {
                  switch (columnMap.format) {
                    case 'number':
                      card[columnMap.field] = parseInt(uploadRow[columnMap.column]);
                      break;
                    case 'array':
                      card[columnMap.field] = uploadRow[columnMap.column].split(';');
                      break;
                    case 'currency':
                      card[columnMap.field] = this.convertToUsd(Number(uploadRow[columnMap.column].replace(/\$/g, '').replace(/,/g, '').replace(/[^0-9.-]+/g, '')));
                      break;
                    case 'date':
                      card[columnMap.field] = new Date(uploadRow[columnMap.column]);
                      break;
                    default:
                      card[columnMap.field] = uploadRow[columnMap.column];
                  }
                } else if (columnMap.required) {
                  if (columnMap.default) {
                    card[columnMap.field] = columnMap.default;
                  } else {
                    missingData[headerKey] = true;
                  }
                }
              });

              card.sold = Boolean(card.dateSold);

              if (card.player === 'Lebron James') {
                card.player = 'LeBron James';
              }

              cards.push({
                ...clone(DEFAULT_CARD_DETAILS),
                ...clone(DEFAULT_CARD_COLLECTION),
                ...card,
              });
            }

            if (Object.keys(missingData).length) {
              this.output = { text: `You are missing some data in one of the required columns: ${Object.keys(missingData).join(', ')}` };
              this.loading = false;

              return;
            }

            this.output = { cards, text: `We successfully read in ${cards.length} cards for you to upload`, progress: 0 };
          } catch (e) {
            this.output = { text: `You have an issue with row ${rowIndex + 1} in your data. Please fix the row and try again.` };
            this.loading = false;

            return;
          }

          this.loading = false;
        });

        reader.readAsBinaryString(file);
      } catch (e) {
        this.loading = false;
        this.output = { text: 'Error reading your file. Please fix the file and try again.' };
      }
    },

    async submit() {
      this.saving = true;

      try {
        for (let i = 0; i < this.output.cards.length; i++) {
          let shouldAddCard = true;
          let card = {
            ...this.output.cards[i],
          };

          if (this.output.cards[i].grader) {
            card.grader = GRADING_COMPANY_MAP[this.output.cards[i].grader.toLowerCase().trim()];
          }

          if (this.isCertUpload) {
            if (!['psa', 'sgc', 'cgc', 'beckett'].includes(card.grader)) {
              shouldAddCard = false;
            } else {
              try {
                const { data: collectionCard } = await firebase.functions().httpsCallable('httpbuildcollectioncard')({
                  cert: card.cert,
                  grader: card.grader,
                });

                if (collectionCard.datePurchased) {
                  collectionCard.datePurchased = new Date(collectionCard.datePurchased);
                }

                card = {
                  ...card,
                  ...collectionCard,
                  investment: card.investment || collectionCard.investment || 100,
                  datePurchased: card.datePurchased || collectionCard.datePurchased || new Date(),
                };

                if (!card.player) {
                  shouldAddCard = false;
                }

                if (card.imageUrl) {
                  card.image = card.imageUrl;
                  delete card.imageUrl;
                }

                if (card.imageBackUrl) {
                  card.imageBack = card.imageBackUrl;
                  delete card.imageBackUrl;
                }

                if (!card.cardId && collectionCard.profileId) {
                  card.useGradedCardAutomatic = true;
                }
              } catch (e) {
                shouldAddCard = false;
                Sentry.captureException(e);
              }
            }
          } else if (card.cardId) {
            const ladderCard = await cardService.getCard(card.cardId);

            if (ladderCard) {
              card.image = ladderCard.image;
              card.label = ladderCard.label;
              card.player = ladderCard.player;
              card.playerIndexId = ladderCard.playerIndexId;
              card.condition = ladderCard.condition;
              card.number = ladderCard.number;
              card.category = ladderCard.category;
              card.year = ladderCard.year;
              card.variation = ladderCard.variation;
              card.set = ladderCard.set;
              card.currentValue = ladderCard.stats?.currentValue || null;
              card.marketValue = ladderCard.stats?.marketValue || null;
              card.guideValue = ladderCard.stats?.guideValue || null;
            } else {
              shouldAddCard = false;
            }
          }

          if (shouldAddCard) {
            if (!card.playerIndexId && card.player && !card.useGradedCardAutomatic && !card.cardId) {
              try {
                const { data: indexResponse } = await firebase.functions().httpsCallable('httpIndexFromDescription')({ description: card.player });

                if (indexResponse.isPlayerIndex) {
                  card.playerIndexId = indexResponse.id;
                } else {
                  card.otherIndexId = indexResponse.id;
                }
              } catch (e) {
                // swallow
              }
            }

            if (!this.isCertUpload && card.ebayQuery && !card.cardId) {
              await this.applyCustomSalesQuery(card);
            }

            const collectionCard = await getCollectionCardToSave(
              card,
              cardService,
              indexService,
              firebase,
              this.collection.id,
              this.user.uid,
            );

            authService.createOrUpdateCollectionCard(
              this.user.uid,
              this.collection.id,
              collectionCard,
            );
          }

          this.output.progress = i + 1;
        }

        this.$toasted.success('Cards uploaded!');
        this.$bus.$emit('collection-changed', { add: true });
        this.closeModal();
      } catch (e) {
        this.$toasted.error('Error uploading the cards');
        Sentry.captureException(e);
      }

      this.saving = false;
    },

    async applyCustomSalesQuery(card) {
      const query = String(card.ebayQuery).trim();

      if (!query) {
        card.ebayQuery = '';
        return;
      }

      card.ebayQuery = query;
      card.multipliers = [];
      card.playerIndexPrice = null;
      card.playerIndexDate = null;
      card.useSalesForValue = true;
      card.useGradedCardAutomatic = false;
      card.dailySales = {};

      if (!card.stats) {
        card.stats = {};
      }

      let sales = [];

      try {
        const data = await searchService.search({
          index: 'salesarchive',
          query,
          page: 0,
          limit: 10,
          sort: 'date',
          direction: 'desc',
        });

        sales = data?.hits || [];
      } catch (e) {
        Sentry.captureException(e);
      }

      const { newestSale } = getDailySalesFromSalesSearch(sales);
      const hasIndex = Boolean(card.playerIndexId || card.otherIndexId);

      if (newestSale) {
        card.stats.newestSale = newestSale;
      }

      // Same default as Custom Sales Query: project the latest sale with an index
      // when one is available, otherwise value the card at the latest sale price.
      card.useLastSoldValue = !hasIndex;

      if (!newestSale && !hasIndex && typeof card.currentValue !== 'number') {
        card.currentValue = card.estimatedCurrentValue || 0;
        card.stats.currentValue = card.currentValue;
      }
    },

    convertToUsd(value) {
      return Number(Number(value / this.currencyRatio).toFixed(this.currencyRatio === 1 ? 2 : 3));
    },

    reset() {
      this.loading = false;
      this.output = {};
      this.saving = false;
    },

    closeModal() {
      this.reset();
      this.open = false;
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.file-upload {
  outline: 1px solid var(--theme-input-border);
  padding: $gp * .5;
  min-height: 320px;
  max-width: 500px;
  margin: 0 auto;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  margin-bottom: $gp;
  border-radius: $border-radius;
  position: relative;
}

.description {
  margin-bottom: $gp * 2;
}

input {
  opacity: 0;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  cursor: pointer;
}

.placeholder {
  max-width: 200px;
  color: $color-gray-5;
  font-size: 14px;

  .icon {
    font-size: 48px;
    margin-bottom: $gp * .5;
  }
}

.loading {
  padding: $gp;
  text-align: center;
}

.output {
  text-align: center;
  margin-bottom: $gp * 2;

  p {
    font-size: 18px;
  }
}
</style>
